/**
 * Databases Service - Fetches database list from database servers
 * Supports multiple database engines (MSSQL, PostgreSQL)
 */

import type { DatabaseEngineId } from '@/core/engines';
import type { DiagnosticsListingAdapter } from '../../../types/connectionDiagnostics';
import { executeQueryV2 } from './queryExecutor';
import { createExplorerExecutionId } from './explorerQueryUtils';
import { getQueries } from './queries';
import { useConnectionStore } from '../../connections/store/connectionStore';
import {
  applyDatabaseLimit,
  getLimitedDatabase,
} from '../../connections/utils/connectionDatabaseLimit';
import { useDatabaseUIStore } from '../store/uiStore';
import { isProbeConnectionId } from '../../../types/probeConnectionId';

/**
 * Item 5 — last production-listing adapter per connectionId. The renderer
 * pushes the value off `executeQueryV2`'s result (set by
 * `mssqlRoutingAdapter`) into this map every time `getDatabases` runs. The
 * connection-diagnostics IPC reads this map back through the renderer-
 * exposed window global so support reports can fingerprint which adapter
 * actually handled the user's last production listing.
 *
 * Two-value enum, fully PII-safe. Production-side has no behavioural impact.
 */
const lastListingAdapterByConnection = new Map<string, DiagnosticsListingAdapter>();

/**
 * C1 (Soporte fixes round 2, Wave C) — the driver-route listing query has no
 * per-request bound (pool `requestTimeout: 0`, no caller ever passed
 * `timeoutMs`), so a wedged connection pended forever with the Data/Schema
 * Compare setup dialogs' dropdowns stuck on "Loading…". This bounds the
 * database-listing query at 30s via `options.timeoutMs` (threaded through
 * `queryWithTimeout`/`batchWithTimeout` on the MSSQL driver route) — it does
 * NOT touch the adapter's `requestTimeout: 0` default, so Query Editor user
 * queries remain unbounded.
 */
const LISTING_TIMEOUT_MS = 30_000;

function recordLastListingAdapter(
  connectionId: string,
  adapter: 'driver' | 'sqltools' | undefined
): void {
  // A connection-dialog probe ("Load databases") is not the user's production
  // listing, and every click mints a fresh probe id: recording it would grow
  // this map per click and never be read back.
  if (isProbeConnectionId(connectionId)) return;
  if (adapter === 'driver' || adapter === 'sqltools') {
    lastListingAdapterByConnection.set(connectionId, adapter);
  }
}

export function getLastListingAdapter(
  connectionId: string
): DiagnosticsListingAdapter | undefined {
  return lastListingAdapterByConnection.get(connectionId);
}

function uniqueStrings(values: Array<string | undefined>): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const trimmed = value?.trim();
    if (!trimmed) continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}

function getConnectionDatabaseFallback(connectionId: string): string | undefined {
  const activeConnection = useConnectionStore.getState().activeConnections.find((c) => c.id === connectionId);
  return activeConnection?.connection.database?.trim() || undefined;
}

/**
 * B3 — central "Limit connection to selected database" enforcement.
 *
 * Resolves the connection, then applies the shared limit rule
 * ({@link getLimitedDatabase}): the trimmed database this connection is scoped
 * to, or `undefined` (⇒ no scoping, unlimited connections behave
 * byte-identically).
 *
 * Reads the connection lazily via `useConnectionStore.getState()` (same
 * call-time pattern as `getConnectionDatabaseFallback` above — no import-time
 * store access): saved `connections` win, falling back to the live
 * `activeConnections[].connection`. This is UI scoping only — it never gates
 * the SQL a user writes (USE / cross-database queries still work).
 */
function getLimitedConnectionDatabase(connectionId: string): string | undefined {
  const state = useConnectionStore.getState();
  const connection =
    state.connections?.find((c) => c.id === connectionId) ??
    state.activeConnections?.find((c) => c.id === connectionId)?.connection;
  return getLimitedDatabase(connection);
}

/**
 * C3 (Soporte fixes round 2, Wave C) — the phases a database listing goes
 * through, reported to callers via {@link GetDatabasesOptions.onPhase} so a
 * spinner can say WHICH slow step it is waiting on:
 *
 * - `'connecting'` — the pre-listing MSSQL driver-pool warm-up
 *   (`ensureMssqlPool`, Slice A). On a cold remote connection this is the
 *   multi-minute step the user was staring at ("Loading…" with no clue).
 * - `'listing'` — connected; the listing query itself is running (including
 *   the Slice R NO_POOL retry and the master-fallback attempt).
 *
 * MSSQL is the only engine with a step the renderer can separate: every other
 * engine opens its connection inside the main process during `executeQueryV2`,
 * invisible from here, so those listings report `'listing'` only. We do NOT
 * invent a fake connect phase for them.
 */
export type DatabasesListingPhase = 'connecting' | 'listing';

/** Options for {@link getDatabases}. */
export type GetDatabasesOptions = {
  /**
   * When true, the central `limitToDatabase` filter is skipped and the full
   * listing is returned. Set ONLY by the Add/Edit Connection dialog so the
   * user can still see (and change) every database while editing the flag.
   */
  ignoreConnectionLimit?: boolean;
  /**
   * C3 — progress callback, invoked once per {@link DatabasesListingPhase} the
   * listing actually enters. Purely advisory: it never changes the resolved
   * {@link GetDatabasesResult}, so a failure during either phase still lands
   * in the same error contract (and therefore the same C2 error UI).
   */
  onPhase?: (phase: DatabasesListingPhase) => void;
};

/**
 * Slice A (pool fix) — best-effort MSSQL pool warm-up before the listing
 * query. On a fresh connection where the main driver pool hasn't been
 * opened yet (e.g. user hasn't opened a Query Editor tab), the listing
 * call would otherwise take the adapter's no-pool branch and return
 * instantly with `code: 'NO_POOL'`. Pre-warming mirrors what Query Editor
 * does on first-tab-open. Errors are swallowed — Slice R retry covers the
 * residual race where the warm-up is in flight when listing fires.
 */
async function ensureMssqlPoolBestEffort(connectionId: string): Promise<void> {
  if (typeof window === 'undefined') return;
  const ensure = window.electronAPI?.ensureMssqlPool;
  if (!ensure) return;
  try {
    await ensure({ connectionId });
  } catch {
    // Best-effort: never let warm-up failures block the listing call.
  }
}

function isMssqlEngine(engine?: DatabaseEngineId | string): boolean {
  return engine === 'mssql';
}

/**
 * C3 E2E seam — hold the listing inside a phase until the spec releases it.
 * Mirrors the `__forceListingFailure` / `__forceTablesFailure` seam family:
 * same `window.__SLICE1_DB_SERVICES__` namespace, a plain per-connection
 * record, unset in production ⇒ zero behavioural impact.
 *
 * Set via
 * `window.__SLICE1_DB_SERVICES__.__holdListing[connectionId] = { connecting: true }`
 * and released by flipping the flag back to false. A hold is capped at 10s so
 * a forgotten flag can never wedge the app permanently.
 *
 * C4 (Soporte fixes round 2, Wave C) — `afterCall` makes the hold per-call
 * capable: `{ listing: true, afterCall: 2 }` only engages starting at the
 * 2nd `getDatabases` call for that connectionId, letting an earlier call
 * (e.g. the Data/Schema Compare setup dialog's SOURCE panel picking the same
 * connection as an already-in-flight TARGET panel) resolve normally while a
 * later one is pinned in flight — needed to deterministically reproduce the
 * "same connection on both sides" swap race without racing a wall clock.
 * Omitting `afterCall` preserves the original "hold from the first call"
 * behaviour every existing caller relies on.
 */
const listingCallCounts = new Map<string, number>();

/**
 * Fix round 2 (review, Minor regression 2) — the counter must stay part of
 * the same "unset seam ⇒ zero behavioural impact" contract every other
 * `__SLICE1_DB_SERVICES__` seam in this file honors. `nextListingCallNumber`
 * mutates a module-level `Map` on every call; gating it on the hold seam
 * actually being armed (`__holdListing` has at least one entry) means a
 * production renderer where no E2E spec has ever touched the seam performs
 * that write literally never — not "harmlessly", but not at all.
 */
function isHoldSeamArmed(): boolean {
  if (typeof window === 'undefined') return false;
  const holds = (
    window as unknown as {
      __SLICE1_DB_SERVICES__?: { __holdListing?: Record<string, unknown> };
    }
  ).__SLICE1_DB_SERVICES__?.__holdListing;
  return !!holds && Object.keys(holds).length > 0;
}

function nextListingCallNumber(connectionId: string): number {
  const next = (listingCallCounts.get(connectionId) ?? 0) + 1;
  listingCallCounts.set(connectionId, next);
  return next;
}

function isListingHeld(
  connectionId: string,
  phase: DatabasesListingPhase,
  callNumber: number
): boolean {
  if (typeof window === 'undefined') return false;
  const holds = (
    window as unknown as {
      __SLICE1_DB_SERVICES__?: {
        __holdListing?: Record<
          string,
          (Partial<Record<DatabasesListingPhase, boolean>> & { afterCall?: number }) | undefined
        >;
      };
    }
  ).__SLICE1_DB_SERVICES__?.__holdListing;
  const entry = holds?.[connectionId];
  if (!entry?.[phase]) return false;
  if (entry.afterCall !== undefined && callNumber < entry.afterCall) return false;
  return true;
}

async function awaitListingHold(
  connectionId: string,
  phase: DatabasesListingPhase,
  callNumber: number
): Promise<void> {
  if (!isListingHeld(connectionId, phase, callNumber)) return;
  const deadline = Date.now() + 10_000;
  while (isListingHeld(connectionId, phase, callNumber) && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 25));
  }
}

/**
 * Slice 1 (pool-noise fix) — derive the accessible-database subset from the
 * MSSQL listing rows. The MSSQL `databases` query selects
 * `HAS_DBACCESS(name) AS has_access` (1 accessible, 0 no access, NULL
 * offline/invalid); only `=== 1` counts.
 *
 * Returns `undefined` (⇒ caller falls back to the full list, preserving
 * legacy behaviour) when the engine is not MSSQL or the result set does not
 * carry the `has_access` column (e.g. the mock engine, the Slice R queued
 * seam, or a server too old to evaluate it). Returns `[]` when the column is
 * present but the login can open none of the listed databases — the caller
 * must treat that as "preload nothing", not "fall back to all".
 */
function extractAccessibleDatabases(
  rows: ReadonlyArray<{ name?: unknown; datname?: unknown; has_access?: unknown }>,
  engine?: DatabaseEngineId | string
): string[] | undefined {
  if (!isMssqlEngine(engine)) return undefined;
  if (rows.length === 0) return undefined;
  if (!('has_access' in rows[0])) return undefined;
  const accessible = rows
    .filter((row) => Number(row.has_access) === 1)
    .map((row) => (row.name ?? row.datname) as string | undefined);
  return uniqueStrings(accessible);
}

/**
 * Outcome of a database listing call.
 *
 * - `'ok'`    — query succeeded with at least one row.
 * - `'empty'` — query succeeded with zero rows. This is meaningfully distinct
 *               from `'error'` because the server actively returned an empty
 *               set (e.g. a contained user with no `HAS_DBACCESS=1` rows).
 *               `listingSucceededEmpty` is set so consumers don't have to
 *               re-derive the distinction from `databases.length`.
 * - `'error'` — the query failed (network error, permission denied, timeout,
 *               etc.). `databases` still contains the fallback list so the UI
 *               has *something* to render while displaying the error tile.
 */
export type DatabasesListingState = 'ok' | 'empty' | 'error';

/**
 * Structured error info returned alongside a failed listing — surfaces the
 * SQL Server number and timeout flag so the connection-diagnostics dialog
 * can render a real cause instead of a generic message.
 *
 * Slice 5b collapsed the legacy `error: string` and `errorDetails` fields
 * into this single structured object. Consumers that just want a human
 * string read `error.message`.
 */
export type DatabasesListingError = {
  message: string;
  sqlErrorCode?: number;
  sqlErrorClass?: number;
  code?: string;
  timedOut?: boolean;
  originalMessage?: string;
};

/**
 * The result of {@link getDatabases}.
 */
export type GetDatabasesResult = {
  /**
   * Three-way state distinguishing an empty result set from a query failure.
   * Always populated.
   */
  state: DatabasesListingState;
  /**
   * The list of database names. On `'error'`, this is the engine's fallback
   * list (the connection's configured database + the engine's hardcoded
   * system list) so consumers always have *something* renderable — but it
   * MUST NOT be presented as authoritative; show "couldn't list" UI instead.
   */
  databases: string[];
  /** Structured error details — populated on `state === 'error'`. */
  error?: DatabasesListingError;
  /**
   * True when the query succeeded and returned zero rows (`state === 'empty'`).
   * Mutually exclusive with `error`.
   */
  listingSucceededEmpty?: boolean;
  /**
   * MSSQL only: the subset of `databases` the connected login can actually
   * open (`HAS_DBACCESS = 1`). Populated only when the listing carried the
   * `has_access` column; `undefined` for every other engine and for legacy
   * results without the column. The Object Explorer still renders every
   * database from `databases`, but background object-preload uses this
   * subset (falling back to `databases` when absent) so it never fires
   * doomed pool opens against databases the login cannot access.
   */
  accessibleDatabases?: string[];
  /** Wall-clock duration of the listing query in milliseconds. */
  durationMs: number;
};

type ExecuteOutcome =
  | { kind: 'ok'; databases: string[]; accessibleDatabases?: string[] }
  | { kind: 'empty' }
  | { kind: 'error'; error: DatabasesListingError };

/**
 * Get all databases for a specific connection.
 *
 * @returns A {@link GetDatabasesResult} describing the listing outcome.
 */
export async function getDatabases(
  connectionId: string,
  engine?: DatabaseEngineId | string,
  options?: GetDatabasesOptions
): Promise<GetDatabasesResult> {
  const queries = getQueries(engine);
  const databaseFallback = getConnectionDatabaseFallback(connectionId);
  const fallbackDatabases = uniqueStrings([databaseFallback, ...queries.fallbackDatabases]);

  // B3 — resolve the connection's limit once (skipped for the Add/Edit dialog
  // opt-out). Applied to every `'ok'` return below so all consumers inherit it.
  const limitedDatabase = options?.ignoreConnectionLimit
    ? undefined
    : getLimitedConnectionDatabase(connectionId);

  // V8 — the fallback list returned on the `'error'` paths (and the test-only
  // forced-failure escape hatch) must inherit the same limit as the `'ok'`
  // returns, otherwise a failed listing leaks the engine's hardcoded system
  // databases to every consumer that renders `databases` on error. Mirrors
  // `finishOk`'s declared-name fallback semantics.
  const scopedFallbackDatabases = limitedDatabase
    ? applyDatabaseLimit(fallbackDatabases, limitedDatabase)
    : fallbackDatabases;

  const start = performance.now();

  // C4 — this connection's ordinal call number, for the per-call `afterCall`
  // hold seam below. Counted once per invocation regardless of what happens
  // next (forced failure, real listing, etc.) so "the Nth call" is unambiguous.
  // Fix round 2 — only counted while the hold seam is armed at all
  // (`isHoldSeamArmed`); see its doc comment above.
  const callNumber = isHoldSeamArmed() ? nextListingCallNumber(connectionId) : 0;

  // Test-only escape hatch. Reproducing a true `state: 'error'` listing
  // failure against `localhost:1434` is not achievable for sysadmin auth
  // because of the master-fallback retry below — this flag lets E2E specs
  // exercise the failure-rendering UI deterministically. Set per-connection
  // via `window.__SLICE1_DB_SERVICES__.__forceListingFailure[connectionId]`.
  if (typeof window !== 'undefined') {
    const overrides = (
      window as unknown as {
        __SLICE1_DB_SERVICES__?: {
          __forceListingFailure?: Record<string, DatabasesListingError | undefined>;
        };
      }
    ).__SLICE1_DB_SERVICES__?.__forceListingFailure;
    const forced = overrides?.[connectionId];
    if (forced) {
      return {
        state: 'error',
        databases: scopedFallbackDatabases,
        error: forced,
        durationMs: Math.round(performance.now() - start),
      };
    }
  }

  async function execute(database: string | undefined): Promise<ExecuteOutcome> {
    // Slice R E2E seam — pop the next queued executeQueryV2 result for this
    // connectionId, when one is queued. Used by the pool-fix E2E spec to
    // simulate a NO_POOL-once-then-success sequence without poking pool
    // internals from the renderer. Keyed per-connection so concurrent
    // tests don't collide. Production code-path is untouched (queue is
    // undefined).
    if (typeof window !== 'undefined') {
      const queues = (
        window as unknown as {
          __SLICE1_DB_SERVICES__?: {
            __queueListingResults?: Record<string, Array<{
              success: boolean;
              error?: {
                message?: string;
                code?: string;
                sqlErrorCode?: number;
                timedOut?: boolean;
              };
              resultSets?: Array<{ rows: Array<{ name?: string; datname?: string; DatabaseName?: string }> }>;
            }> | undefined>;
          };
        }
      ).__SLICE1_DB_SERVICES__?.__queueListingResults;
      const queue = queues?.[connectionId];
      if (queue && queue.length > 0) {
        const queued = queue.shift()!;
        if (!queued.success) {
          return {
            kind: 'error',
            error: {
              message: queued.error?.message || 'Queued listing failure',
              ...(queued.error?.sqlErrorCode !== undefined ? { sqlErrorCode: queued.error.sqlErrorCode } : {}),
              ...(queued.error?.code !== undefined ? { code: queued.error.code } : {}),
              ...(queued.error?.timedOut ? { timedOut: true } : {}),
            },
          };
        }
        const rows = queued.resultSets?.[0]?.rows ?? [];
        if (rows.length === 0) return { kind: 'empty' };
        const databases = rows
          .map((row) => (row.name ?? row.datname ?? row.DatabaseName) as string | undefined)
          .filter((name): name is string => typeof name === 'string' && !!name.trim());
        return { kind: 'ok', databases: uniqueStrings(databases) };
      }
    }

    const result = await executeQueryV2({
      connectionId,
      sql: queries.databases,
      database,
      executionId: createExplorerExecutionId(connectionId),
      options: { timeoutMs: LISTING_TIMEOUT_MS },
    });

    // Item 5 — capture the adapter that handled this listing call. Both
    // success and failure paths get recorded so the diagnostics report
    // surfaces a value even when the listing failed (which is the case
    // we care about for the bug-fingerprinting use case).
    recordLastListingAdapter(connectionId, result.executionAdapter);

    if (!result.success) {
      const message = result.error?.message || 'Unknown error';
      return {
        kind: 'error',
        error: {
          message,
          ...(result.error?.sqlErrorCode !== undefined ? { sqlErrorCode: result.error.sqlErrorCode } : {}),
          ...(result.error?.sqlErrorClass !== undefined ? { sqlErrorClass: result.error.sqlErrorClass } : {}),
          ...(result.error?.code !== undefined ? { code: result.error.code } : {}),
          ...(result.error?.timedOut ? { timedOut: true } : {}),
          ...(result.error?.originalMessage ? { originalMessage: result.error.originalMessage } : {}),
        },
      };
    }

    const rows = result.resultSets?.[0]?.rows || [];
    if (rows.length === 0) {
      // Defense-in-depth: a "successful" result whose rows were windowed away
      // (rowCount > 0 but inline rows stripped, or a result-cache URI present)
      // means the execution path returned a windowed result this service
      // cannot read — a routing bug, NOT an empty server. Classifying it as
      // 'empty' produced the false "No databases found or no permission to
      // view databases" on SSTS-routed connections; surface it as an error so
      // any future regression is loud instead of silently lying.
      const windowedAway =
        (result.resultSets?.[0]?.rowCount ?? 0) > 0 ||
        Boolean(result.resultSetCacheUris?.some(Boolean));
      if (windowedAway) {
        return {
          kind: 'error',
          error: {
            message:
              'Database listing returned windowed rows the listing service cannot read (internal routing error).',
            code: 'WINDOWED_LISTING',
          },
        };
      }
      return { kind: 'empty' };
    }

    // Extract database names from the result (first column of each row)
    // PostgreSQL uses 'name' alias, MSSQL returns 'name' column, and kusto's
    // bare `.show databases` returns 'DatabaseName' (it can't alias — the
    // Azure Monitor ADX proxy 400s on a piped control command).
    const databases = rows
      .map((row) => (row.name ?? row.datname ?? row.DatabaseName) as string | undefined)
      .filter((name): name is string => typeof name === 'string' && !!name.trim());
    const accessibleDatabases = extractAccessibleDatabases(rows, engine);
    return {
      kind: 'ok',
      databases: uniqueStrings(databases),
      ...(accessibleDatabases !== undefined ? { accessibleDatabases } : {}),
    };
  }

  const finish = (result: GetDatabasesResult): GetDatabasesResult => ({
    ...result,
    durationMs: Math.round(performance.now() - start),
  });

  // Finish an `'ok'` listing, applying the B3 limit filter to both the full
  // list and the accessible subset when the connection is limited. Unlimited
  // connections pass through untouched.
  const finishOk = (
    databases: string[],
    accessibleDatabases?: string[]
  ): GetDatabasesResult => {
    const scopedDatabases = limitedDatabase
      ? applyDatabaseLimit(databases, limitedDatabase)
      : databases;
    const scopedAccessible =
      accessibleDatabases !== undefined && limitedDatabase
        ? applyDatabaseLimit(accessibleDatabases, limitedDatabase)
        : accessibleDatabases;
    return finish({
      state: 'ok',
      databases: scopedDatabases,
      ...(scopedAccessible !== undefined ? { accessibleDatabases: scopedAccessible } : {}),
      durationMs: 0,
    });
  };

  // Slice 8 telemetry: fire `feature:db_list_failed` once per failed
  // listing, after the structured error payload is finalised. Properties
  // are low-cardinality categorical / nullable integer code — never
  // includes hostnames, DB names, or error messages.
  const trackListingFailed = (errorPayload: DatabasesListingError | undefined): void => {
    const sqlErrorCode =
      typeof errorPayload?.sqlErrorCode === 'number' ? errorPayload.sqlErrorCode : null;
    // 'master' when we ran the listing against the engine's default
    // database (most common: Object Explorer connecting to a server with
    // no per-connection database set). 'fallback_db' when we ran it
    // against the connection's configured default database.
    const source: 'master' | 'fallback_db' =
      !databaseFallback || databaseFallback === queries.defaultDatabase ? 'master' : 'fallback_db';
    try {
      window.electronAPI?.analytics
        ?.track('feature:db_list_failed', {
          engine: typeof engine === 'string' ? engine : 'mssql',
          sql_error_code: sqlErrorCode,
          source,
        })
        .catch(() => {
          // Analytics is fire-and-forget.
        });
    } catch {
      // Defensive: never let analytics break the listing path.
    }
  };

  try {
    // Slice A — pre-open the MSSQL driver pool before the listing query.
    // No-op for non-MSSQL engines.
    //
    // C3 — this warm-up IS the "connecting" phase: it is the only pre-listing
    // step the renderer can separate, and on a cold remote server it is where
    // the minutes go. The `isListingHeld` term is the E2E seam standing in for
    // it against the mock engine (which has no driver pool); it is false in
    // production, where the phase is emitted for MSSQL and nothing else.
    if (isMssqlEngine(engine) || isListingHeld(connectionId, 'connecting', callNumber)) {
      options?.onPhase?.('connecting');
      if (isMssqlEngine(engine)) {
        await ensureMssqlPoolBestEffort(connectionId);
      }
      await awaitListingHold(connectionId, 'connecting', callNumber);
    }

    options?.onPhase?.('listing');
    await awaitListingHold(connectionId, 'listing', callNumber);

    let primaryAttempt = await execute(databaseFallback);

    // Slice R — retry exactly once, after 500 ms, when the first attempt
    // failed with `code: 'NO_POOL'`. This covers the residual race where
    // Slice A's pool-open is still in flight when the listing query fires.
    // Do NOT retry on any other code (sqlErrorCode, timedOut, etc.) — those
    // are deterministic failures and a retry only doubles user-perceived
    // latency.
    if (
      primaryAttempt.kind === 'error' &&
      primaryAttempt.error.code === 'NO_POOL' &&
      isMssqlEngine(engine)
    ) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      primaryAttempt = await execute(databaseFallback);
    }

    if (primaryAttempt.kind === 'ok') {
      return finishOk(primaryAttempt.databases, primaryAttempt.accessibleDatabases);
    }
    if (primaryAttempt.kind === 'empty') {
      return finish({
        state: 'empty',
        databases: [],
        listingSucceededEmpty: true,
        durationMs: 0,
      });
    }

    const fallbackAttemptDatabase =
      databaseFallback !== queries.defaultDatabase ? queries.defaultDatabase : undefined;
    const fallbackAttempt = await execute(fallbackAttemptDatabase);
    if (fallbackAttempt.kind === 'ok') {
      return finishOk(
        uniqueStrings([...fallbackAttempt.databases, databaseFallback]),
        // The connection's own configured database is by definition
        // accessible (we are connected to it), so fold it into the
        // accessible subset alongside the fallback listing's `HAS_DBACCESS`
        // rows.
        fallbackAttempt.accessibleDatabases !== undefined
          ? uniqueStrings([...fallbackAttempt.accessibleDatabases, databaseFallback])
          : undefined
      );
    }
    if (fallbackAttempt.kind === 'empty') {
      return finish({
        state: 'empty',
        databases: [],
        listingSucceededEmpty: true,
        durationMs: 0,
      });
    }

    const errorPayload = primaryAttempt.error;
    console.error(`[DatabasesService] Error getting databases: ${errorPayload.message}`);
    trackListingFailed(errorPayload);
    return finish({
      state: 'error',
      databases: scopedFallbackDatabases,
      error: errorPayload,
      durationMs: 0,
    });
  } catch (error) {
    console.error('[DatabasesService] Error in getDatabases:', error);
    const message = error instanceof Error ? error.message : 'Unknown error getting databases';
    const errorPayload: DatabasesListingError = { message };
    trackListingFailed(errorPayload);
    return finish({
      state: 'error',
      databases: scopedFallbackDatabases,
      error: errorPayload,
      durationMs: 0,
    });
  }
}

/**
 * C4 E2E seam — clear the per-connectionId call counters that back the
 * `afterCall` hold above. `listingCallCounts` lives for the lifetime of the
 * renderer module (i.e. the whole worker-scoped Electron app across every
 * test in a spec file), so a Playwright retry of a test that reuses the same
 * connectionId would otherwise start from a stale, non-zero count. Specs
 * call this in `beforeEach` before arming an `afterCall` hold. No-op / unused
 * in production.
 */
function resetListingCallCounts(): void {
  listingCallCounts.clear();
}

// Expose getDatabases on a dedicated renderer-side window global so E2E specs
// can drive it directly without round-tripping through the UI. We use a
// separate global (`__SLICE1_DB_SERVICES__`) rather than `__TEST_STORES__`
// because the latter is reassigned wholesale by `src/features/sessions/index.ts`
// after this module loads, which would wipe any field we attached. In
// production renderers this is a single function reference on a window
// property — no behavioural impact, no PII, no cost.
if (typeof window !== 'undefined') {
  (window as unknown as {
    __SLICE1_DB_SERVICES__?: {
      getDatabases: typeof getDatabases;
      getLastListingAdapter: typeof getLastListingAdapter;
      resetListingCallCounts: typeof resetListingCallCounts;
    };
  }).__SLICE1_DB_SERVICES__ = { getDatabases, getLastListingAdapter, resetListingCallCounts };
}

/**
 * DevTools helper — queue NO_POOL listing failures for the user's currently
 * selected connection (or an explicit `connectionId`) so the next Object-
 * Explorer refresh exercises the Slice 9 / Slice 11 failure surface end-to-end.
 *
 * Usage from DevTools (no args needed):
 *   window.__forceListingNoPool()
 *
 * Resolution order for the target connection:
 *   1. explicit `opts.connectionId` argument
 *   2. `useDatabaseUIStore.selectedConnectionId` (the connection currently
 *      focused in the Object Explorer tree)
 *   3. the first entry of `useConnectionStore.activeConnections`
 *
 * Three NO_POOL entries are queued by default — enough to defeat both the
 * Slice R primary retry (entry 2) and the master-fallback re-attempt
 * (entry 3) so `getDatabases` ends in `state: 'error'` with `code: 'NO_POOL'`
 * and the failure tile renders. Pass `opts.count` to override.
 *
 * After calling, refresh Object Explorer (right-click Databases → Refresh,
 * or collapse + re-expand) to drive the listing through the seam.
 */
function forceListingNoPool(
  opts: { connectionId?: string; count?: number } = {}
): { connectionId: string; queued: number } | { error: string } {
  if (typeof window === 'undefined') return { error: 'window unavailable' };

  let resolvedId = opts.connectionId;
  if (!resolvedId) {
    resolvedId = useDatabaseUIStore.getState().selectedConnectionId;
  }
  if (!resolvedId) {
    const active = useConnectionStore.getState().activeConnections;
    resolvedId = active[0]?.id;
  }
  if (!resolvedId) {
    return { error: 'No connection found. Connect to a server first, or pass { connectionId }.' };
  }

  const count = Math.max(1, Math.min(10, opts.count ?? 3));
  const ns = (
    window as unknown as {
      __SLICE1_DB_SERVICES__?: {
        __queueListingResults?: Record<string, Array<{
          success: boolean;
          error?: { message?: string; code?: string; sqlErrorCode?: number; timedOut?: boolean };
          resultSets?: Array<{ rows: Array<{ name?: string; datname?: string }> }>;
        }> | undefined>;
      };
    }
  ).__SLICE1_DB_SERVICES__;
  if (!ns) return { error: '__SLICE1_DB_SERVICES__ not initialised' };
  const queues = ns.__queueListingResults ?? (ns.__queueListingResults = {});
  const entries = Array.from({ length: count }, () => ({
    success: false,
    error: { message: 'Connection is not active. Please reconnect to the server.', code: 'NO_POOL' as const },
  }));
  queues[resolvedId] = entries;
  console.log(
    `[forceListingNoPool] Queued ${count} NO_POOL listing failures for connection ${resolvedId}. ` +
      `Refresh Object Explorer (right-click Databases → Refresh) to trigger.`
  );
  return { connectionId: resolvedId, queued: count };
}

if (typeof window !== 'undefined') {
  (window as unknown as { __forceListingNoPool?: typeof forceListingNoPool }).__forceListingNoPool =
    forceListingNoPool;
}
